/**
 * F15 — ExecutionPlannerScreen (v2).
 * DataTable of stage definitions per phase.
 * Add/edit via FormModal + executionStageSchema (prerequisite dropdown reads
 * useWbsStages() → binds prerequisiteWbsStageId — v2 rename from prerequisiteStatusTypeId).
 * Delete via ConfirmationModal.
 * Write controls only when usePermission().can("project.edit", projectId).
 * Reads: §7 GET/POST/PUT/DELETE /api/interior/execution-plan
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  getExecutionPlan,
  createExecutionStage,
  updateExecutionStage,
  deleteExecutionStage,
} from "../../services/interiorExecution/index";
import { executionStageSchema } from "../../services/interiorExecution/schemas";
import { useWbsStages } from "../../constants/wbsStages";
import { FormModal, Form, Field, FormSubmit } from "../../components/form";
import ConfirmationModal from "../../components/ConfirmationModal";
import { DeleteButton, EditButton, IconButton } from "../../components/Buttons";
import usePermission from "../../hooks/usePermission";
import { PERMISSIONS } from "../../constants/permissions";

/**
 * @param {{phaseId?: number|string, projectId?: number|string}} props
 *   phaseId: from useParams or parent mount (required for data fetch).
 *   projectId: for permission check.
 */
const ExecutionPlannerScreen = ({ phaseId: phaseIdProp, projectId: projectIdProp }) => {
  const { phaseId: phaseIdParam } = useParams();
  const phaseId = phaseIdProp ?? phaseIdParam;
  const projectId = projectIdProp ?? null;

  const { can } = usePermission();
  const canEdit = can(PERMISSIONS.PROJECT_EDIT, projectId);

  const { stages: catalogStages, loading: stagesLoading } = useWbsStages();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const loadPlan = useCallback(async () => {
    if (!phaseId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getExecutionPlan({ projectPhaseId: phaseId });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load execution plan.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [phaseId]);

  useEffect(() => { loadPlan(); }, [loadPlan]);

  const handleCreate = async (values) => {
    try {
      const created = await createExecutionStage({ ...values, projectPhaseId: phaseId });
      setRows(prev => [...prev, created]);
      toast.success("Stage created.");
      setModalOpen(false);
    } catch (err) {
      toast.error(err?.detail || "Failed to create stage.");
    }
  };

  const handleUpdate = async (values) => {
    if (!editingRow) return;
    try {
      const updated = await updateExecutionStage(editingRow.executionStageId, values);
      setRows(prev => prev.map(r => r.executionStageId === editingRow.executionStageId ? updated : r));
      toast.success("Stage updated.");
      setModalOpen(false);
      setEditingRow(null);
    } catch (err) {
      toast.error(err?.detail || "Failed to update stage.");
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteExecutionStage(rowToDelete.executionStageId);
      setRows(prev => prev.filter(r => r.executionStageId !== rowToDelete.executionStageId));
      toast.success("Stage deleted.");
    } catch (err) {
      toast.error(err?.detail || "Failed to delete stage.");
    } finally {
      setDeleteModalOpen(false);
      setRowToDelete(null);
    }
  };

  const openEdit = (row) => { setEditingRow(row); setModalOpen(true); };
  const openCreate = () => { setEditingRow(null); setModalOpen(true); };
  const openDelete = (row) => { setRowToDelete(row); setDeleteModalOpen(true); };

  if (loading || stagesLoading) {
    return <div className="p-6 text-center text-slate-500 animate-pulse">Loading execution plan…</div>;
  }
  if (error) {
    return <div className="p-6 text-center text-red-500">{error}</div>;
  }

  // prerequisite options: active catalog stages (for dropdown)
  const prerequisiteOptions = catalogStages
    .filter(s => s.isActive)
    .map(s => ({ value: s.wbsStageId, label: s.label || s.typeName }));

  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-900">Execution Planner</h2>
        {canEdit && (
          <IconButton icon={<FaPlus className="mr-2" />} left onClick={openCreate}>
            Add Stage
          </IconButton>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Key</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Label</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Order</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Prereq WBS Stage</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Duration (days)</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Buffer (days)</th>
              <th className="text-left px-4 py-2.5 text-slate-500 font-medium">Active</th>
              {canEdit && <th className="px-4 py-2.5" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(row => (
              <tr key={row.executionStageId} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">{row.stageKey}</td>
                <td className="px-4 py-2.5 text-slate-700">{row.stageLabel}</td>
                <td className="px-4 py-2.5 text-slate-600">{row.sequenceOrder}</td>
                {/* v2: prerequisiteWbsStageId (not prerequisiteStatusTypeId) */}
                <td className="px-4 py-2.5 text-slate-600">
                  {catalogStages.find(s => s.wbsStageId === row.prerequisiteWbsStageId)?.label
                    || (row.prerequisiteWbsStageId != null ? `#${row.prerequisiteWbsStageId}` : "—")}
                </td>
                <td className="px-4 py-2.5 text-slate-600">{row.estimatedDurationDays}</td>
                <td className="px-4 py-2.5 text-slate-600">{row.bufferDays ?? 0}</td>
                <td className="px-4 py-2.5">
                  <span className={row.isActive ? "text-emerald-600" : "text-slate-400"}>
                    {row.isActive ? "Yes" : "No"}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-4 py-2.5">
                    <div className="flex gap-2 justify-end">
                      <EditButton onClick={() => openEdit(row)} />
                      <DeleteButton onClick={() => openDelete(row)} />
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-4 py-8 text-center text-slate-400">
                  No stages defined yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit modal */}
      <FormModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingRow(null); }}
        title={editingRow ? "Edit Stage" : "Add Stage"}
        size="md"
      >
        <Form
          key={editingRow?.executionStageId ?? "new"}
          schema={executionStageSchema}
          defaultValues={editingRow ? {
            stageKey: editingRow.stageKey,
            stageLabel: editingRow.stageLabel,
            sequenceOrder: editingRow.sequenceOrder,
            // v2: prerequisiteWbsStageId bound here
            prerequisiteWbsStageId: editingRow.prerequisiteWbsStageId ?? null,
            estimatedDurationDays: editingRow.estimatedDurationDays,
            bufferDays: editingRow.bufferDays ?? 0,
            isActive: editingRow.isActive ?? true,
          } : {}}
          onSubmit={editingRow ? handleUpdate : handleCreate}
        >
          <Field name="stageKey" label="Stage Key" placeholder="e.g. FLOORING" required />
          <Field name="stageLabel" label="Stage Label" placeholder="e.g. Flooring & Tiling" required />
          <Field name="sequenceOrder" label="Sequence Order" type="number" required />
          {/* v2: prerequisiteWbsStageId dropdown — bound to wbsStageId from catalog */}
          <Field
            name="prerequisiteWbsStageId"
            label="Prerequisite WBS Stage"
            type="select"
            options={[{ value: null, label: "None" }, ...prerequisiteOptions]}
          />
          <Field name="estimatedDurationDays" label="Estimated Duration (days)" type="number" required />
          <Field name="bufferDays" label="Buffer (days)" type="number" />
          <Field name="isActive" label="Active" type="checkbox" />
          <FormSubmit loadingLabel="Saving…">
            {editingRow ? "Update Stage" : "Create Stage"}
          </FormSubmit>
        </Form>
      </FormModal>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        message={`Delete stage "${rowToDelete?.stageLabel}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => { setDeleteModalOpen(false); setRowToDelete(null); }}
      />
    </div>
  );
};

export default ExecutionPlannerScreen;
